import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Menu, 
  X, 
  ChevronDown, 
  Shield, 
  Code, 
  BookOpen, 
  FileText
} from 'lucide-react'
import { navigation } from '../lib/data'
import { cn } from '../lib/utils'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const isActive = (path: string) => location.pathname === path

  const serviceItems = [
    { label: 'Security Services', href: '/security', icon: Shield, description: 'Penetration testing, compliance, and security consulting' },
    { label: 'Development Services', href: '/development', icon: Code, description: 'Full-stack development and architecture design' },
    { label: 'Case Studies', href: '/case-studies', icon: BookOpen, description: 'Real-world examples of our work' },
    { label: 'Insights', href: '/insights', icon: FileText, description: 'Latest security and development insights' }
  ]

  return (
    <nav className={cn(
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled ? 'bg-dsr-bg/80 backdrop-blur-md border-b border-white/10' : 'bg-transparent'
    )}>
      <div className="container-max px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="w-8 h-8 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl lg:text-2xl font-bold text-gradient group-hover:scale-105 transition-transform">
              DevSecRoot
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigation.main.map((item) => (
              <div key={item.href} className="relative group">
                {item.children ? (
                  <div className="relative">
                    <button className="flex items-center space-x-1 text-sm font-medium text-dsr-text hover:text-dsr-accent transition-colors">
                      <span>{item.label}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    
                    {/* Mega Menu */}
                    <div className="absolute top-full left-0 mt-2 w-96 bg-dsr-card backdrop-blur-md border border-white/10 rounded-2xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                      <div className="p-6 grid grid-cols-2 gap-4">
                        {serviceItems.map((service) => (
                          <Link
                            key={service.href}
                            to={service.href}
                            className="group/item p-4 rounded-xl hover:bg-white/5 transition-colors"
                          >
                            <div className="flex items-start space-x-3">
                              <service.icon className="w-5 h-5 text-dsr-accent mt-0.5" />
                              <div>
                                <h3 className="font-medium text-dsr-text group-hover/item:text-dsr-accent transition-colors">
                                  {service.label}
                                </h3>
                                <p className="text-sm text-dsr-text/70 mt-1">
                                  {service.description}
                                </p>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      'text-sm font-medium transition-colors link-underline',
                      isActive(item.href) 
                        ? 'text-dsr-accent' 
                        : 'text-dsr-text hover:text-dsr-accent'
                    )}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link to="/contact" className="btn-primary">
              Book Assessment
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-dsr-text hover:text-dsr-accent transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-dsr-card backdrop-blur-md border-t border-white/10"
          >
            <div className="container-max px-4 py-6 space-y-4">
              {navigation.main.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    'block py-3 text-base font-medium transition-colors',
                    isActive(item.href) 
                      ? 'text-dsr-accent' 
                      : 'text-dsr-text hover:text-dsr-accent'
                  )}
                >
                  {item.label}
                </Link>
              ))}
              
              <div className="pt-4 border-t border-white/10">
                <Link 
                  to="/contact" 
                  onClick={() => setIsOpen(false)}
                  className="btn-primary w-full text-center"
                >
                  Book Assessment
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default Navbar
